import type { ReactNode } from "react";

/**
 * Shared `<FieldRow>` for drawer sections — label + hint + input + error
 * (in that DOM order). Extracted in WU-3 T3.9 from inline JSX duplicated
 * across IdentitySection / DisplaySection / BehaviorSection.
 *
 * Two kinds of rows render through this:
 *   - Text input / select: label-on-top with the input below
 *   - Checkbox: input-then-label on the same flex baseline
 *     (the legacy `field-basics` row stacked input + label-text vertically;
 *     R-11 fix straightens that to a horizontal pair.)
 *
 * The component intentionally leaves the input element to the caller so
 * each section can wire its own handlers (different state, different
 * commit semantics) without us building a generic input abstraction.
 */

export interface FieldRowProps {
  htmlFor: string;
  label: ReactNode;
  hint?: ReactNode;
  /** Caller supplies the actual input/select element. */
  children: ReactNode;
  /** Optional inline error rendered with role="alert". */
  error?: string | null;
}

export function FieldRow({ htmlFor, label, hint, children, error }: FieldRowProps) {
  return (
    <div className="field-section__row">
      <label className="field-section__label" htmlFor={htmlFor}>
        {label}
        {hint && <span className="field-section__hint">{hint}</span>}
      </label>
      {children}
      {error && (
        <div className="field-section__error" role="alert">{error}</div>
      )}
    </div>
  );
}

/**
 * Checkbox variant — the input renders to the LEFT of the label so they
 * share a horizontal baseline. Used by Required + ForceUppercase rows
 * (R-11 CSS centering fix).
 */
export interface FieldCheckboxRowProps {
  id: string;
  checked: boolean;
  label: ReactNode;
  hint?: ReactNode;
  onChange: (next: boolean) => void;
}

export function FieldCheckboxRow({ id, checked, label, hint, onChange }: FieldCheckboxRowProps) {
  return (
    <div className="field-section__row">
      <div className="field-section__checkbox-row">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={id} className="field-section__checkbox-label">{label}</label>
      </div>
      {hint && <span className="field-section__hint">{hint}</span>}
    </div>
  );
}

/**
 * Shared keydown handler — Enter blurs the active input, which triggers
 * the on-blur commit path used uniformly by every text input in the
 * field-editor sections.
 */
export function blurOnEnter(e: React.KeyboardEvent<HTMLInputElement>): void {
  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
}

/**
 * Trim a draft string and collapse empty / all-whitespace input to
 * `undefined`. Mirrors the `snapshot-builder.emptyToUndefined` predicate
 * so UI commit semantics stay aligned with the persistence boundary —
 * neither the editor nor the snapshot writes empty strings to fields
 * whose `?? ''` consumers expect `undefined` for "unset."
 *
 * Used by every section's text/textarea commit paths (DisplaySection,
 * BehaviorSection). (R-123 removed OcrSection — per-field OCR config now
 * lives on the OCR Profiles tab.)
 */
export function emptyToUndefined(s: string): string | undefined {
  const trimmed = s.trim();
  return trimmed === "" ? undefined : trimmed;
}
